import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Box,
  Flex,
  Text,
  Center,
  Divider,
  Spinner,
  Show,
} from "@chakra-ui/react";
import { useBlog } from "mlp-sdk/hooks/useBlog";
import {
  BlogCard,
  Pagination,
  Search,
  BreadCrum,
  AppContext,
  PageContext,
  Footer,
  Header,
  Newsletter,
} from "mlp-sdk";
import { useRouter } from "next/router";
import { PageProvider } from "providers/PageProvider";
import { NextPage } from "next";

import {
  // fetchPageData,
  getSectionDataByKey,
  prepareNavData,
} from "mlp-sdk/utils/common";
import { useStaderTVL } from "mlp-sdk/hooks/useStaderTVL";
import { trackEvent } from "mlp-sdk/utils/firebase";
import {
  LP_BLOGS_FILTER_CLICK,
  LP_BLOGS_PAGE,
} from "mlp-sdk/constants/firebase";
import PageHead from "@components/PageHead/PageHead";

import navData from "@constants/navData.json";
import pageData from "@constants/pageData.json";

const ALL_CHAINS = { label: "All chains", value: "all" };
const SD = { label: "SD", value: "sd" };
const HEDERA = { label: "Hedera", value: "hedera" };
const POLYGON = { label: "Polygon", value: "polygon" };
const ETHEREUM = { label: "Ethereum", value: "ethereum" };
const STAKING_BASICS = { label: "Staking basics", value: "staking-basics" };
const BNB = { label: "BNB", value: "bnbchain" };

const BreadCrumData = [
  {
    label: "Home",
    path: "/",
  },
  {
    label: "Our blogs",
    path: "/blogs",
  },
];

const NAVITEMS = [
  ALL_CHAINS,
  POLYGON,
  BNB,
  ETHEREUM,
  HEDERA,
  // FANTOM,
  // NEAR,
  // TERRA,
  SD,
  STAKING_BASICS,
];

const Blog = () => {
  const { page } = useContext(PageContext);
  const { setTagQuery } = useContext(AppContext);

  const pageData = page.data.pageData;

  const subscriptionData = getSectionDataByKey(pageData, "subscription");

  const [activeMenuKey, setActiveMenuKey] = useState("all");
  const [isBannerScrolled, setIsBannerScrolled] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [currentPage] = useState(1);
  const router = useRouter();
  const { blogs, loading } = useBlog({
    page: currentPage,
    search,
  });

  const { tvl } = useStaderTVL();

  const { navData, networks } = useMemo(
    () => prepareNavData(page.data.navData, tvl),
    [page.data.navData, tvl]
  );

  useEffect(() => {
    trackEvent(LP_BLOGS_PAGE);
  });

  useEffect(() => {
    const heroSection = document.getElementById("blogHeroSection");
    const observer = new IntersectionObserver(
      (entries) => {
        for (let entry of entries) {
          if (entry.isIntersecting) {
            setIsBannerScrolled(true);
          } else {
            setIsBannerScrolled(false);
          }
        }
      },
      {
        threshold: 1,
      }
    );
    heroSection && observer.observe(heroSection);

    // cleanup function
    return () => {
      heroSection && observer.unobserve(heroSection);
    };
  }, []);

  useEffect(() => {
    if (router.query?.tag) {
      const validTag = NAVITEMS.filter(
        (item: any) => item.value === router.query?.tag
      );
      setActiveMenuKey(validTag.length > 0 ? validTag[0].value : "");
      if (validTag.length == 0) {
        router.replace({
          query: {},
        });
      }
    } else {
      setActiveMenuKey("all");
    }
  }, [router]);

  const onSubHeaderChange = useCallback((tag: string) => {
    trackEvent(LP_BLOGS_FILTER_CLICK, {
      filter: tag,
      type: "blogs_page",
    });
    const params: any = {
      page: 1,
    };
    if (tag !== "") {
      params.tag = tag;
    }
    setTagQuery(params);
    router.push({
      query: params,
    });
    // setTimeout(() => {
    //   setActiveMenuKey(item);
    // }, 100);
  }, []);

  const handlePage = useCallback(
    (page: number) => {
      const params = {
        ...router.query,
        page: page,
      };
      setTagQuery(params);

      router.push({
        query: params,
      });
    },
    [router]
  );

  const handleSearch = useCallback((text: string) => {
    setSearch(text);
  }, []);

  return (
    <>
      <PageHead page="blogs" />
      <Header
        data={navData}
        networks={networks}
        bgColor={isBannerScrolled ? "transparent" : undefined}
        tvl={tvl}
      />
      {/* // TODO Temporary solution by margin (as position changed to sticky) height should be dynamic */}
      <Box
        id="blogHeroSection"
        className="blogBanner"
        h={{ base: 248, lg: 403 }}
        mt={{ base: 0, lg: "-86px" }}
      >
        <Show below="lg">
          <BreadCrum data={BreadCrumData} />
        </Show>
        <Center
          mt="120px"
          h="calc(100% - 100px)"
          w="100%"
          justifyContent={{ base: "flex-end", md: "center" }}
          flexDir="column"
          pb={{ base: "84px", lg: "80px" }}
        >
          <Show above="lg">
            <Box w="100%" maxW="1192px">
              <BreadCrum ml={0} data={BreadCrumData} />
            </Box>
          </Show>
          <Text fontWeight="700" fontSize={{ base: "32px", md: "64px" }}>
            Our blogs
          </Text>
          <Search onSearch={handleSearch} />
        </Center>
      </Box>
      <Box
        bg="#FCF0D6"
        position={{ base: "sticky", lg: "unset" }}
        top="57px"
        mt="-1px"
        w="100%"
        maxW="1920px"
        pt="10px"
        zIndex={{ base: 2, lg: 0 }}
      >
        <Box overflow="auto">
          <Flex
            w="100%"
            gap="1.5rem"
            justifyContent={{ base: "flex-start", lg: "center" }}
            ml={{ base: "36px", lg: 0 }}
          >
            {NAVITEMS.map((item: any) => (
              <Center
                key={item.label}
                cursor="pointer"
                flexDir="column"
                p="0 12px 0"
                minW="fit-content"
                onClick={() => {
                  onSubHeaderChange(item.value);
                }}
              >
                <Text
                  color={
                    activeMenuKey === item.value ? "black" : "textSecondary"
                  }
                  mb="1rem"
                  fontWeight={activeMenuKey === item.value ? 600 : 500}
                >
                  {item.label}
                </Text>
                <Divider
                  w="100%"
                  border="1px solid"
                  borderColor={
                    activeMenuKey === item.value ? "#07C166" : "transparent"
                  }
                  borderTopRadius={4}
                />
              </Center>
            ))}
          </Flex>
        </Box>
        <Divider border="1px solid" borderColor="#AEDFCA" w="100%" />
      </Box>
      <Center
        flexDirection="row"
        flexWrap="wrap"
        gap={{ base: "24px", lg: "40px" }}
        p={{ base: "40px 0px 64px", lg: "120px 160px 64px 160px" }}
        w="100%"
        maxW="1512px"
      >
        {!loading &&
          blogs &&
          blogs.posts?.map((item: any) => {
            return (
              <BlogCard key={item.id} data={item} variant={2} isInternal />
            );
          })}
        {loading && (
          <Center h="620px">
            <Spinner size="lg" />
          </Center>
        )}
      </Center>
      {blogs && (
        <Pagination
          page={blogs.meta.pagination.page}
          pages={blogs.meta.pagination.pages}
          setCurrentPage={(page: number) => (loading ? null : handlePage(page))}
          loading={loading}
        />
      )}

      <Newsletter data={subscriptionData} networks={networks} />
      <Footer data={navData} isEmailModalRequired={false} />
    </>
  );
};

const BlogPage: NextPage = ({ page }: any) => {
  return (
    <PageProvider page={page}>
      <Blog />
    </PageProvider>
  );
};

export async function getStaticProps() {
  return {
    props: {
      page: {
        data: {
          navData,
          pageData,
        },
        error: { navError: null, pageError: null },
      },
    },
  };
}

export default BlogPage;
